import { useCallback, useState } from "react";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { useMonitoringEvents, useMonitoringMetrics } from "../api";
import type { EventGroup } from "../types";
import { EventGroups } from "./events/event-groups";
import { EventLog } from "./events/event-log";
import { EventsOverview } from "./events/events-overview";
import {
  DEFAULT_LOG_FILTERS,
  EVENT_LEVELS,
  useBranchNames,
  useEventQueryFilters,
  type LogFilterState,
} from "./events/utils";

const LOG_ID = "monitoring-event-log";

/**
 * Errors & events: how many errors / warnings the backend recorded over the board window, the problems
 * grouped by fingerprint (most frequent first) and the full event log with filters, details JSON and
 * tracebacks. A click on a group narrows the log to it.
 */
export function EventsTab() {
  const metrics = useMonitoringMetrics();
  const branchNames = useBranchNames();
  const reducedMotion = usePrefersReducedMotion();
  const [state, setState] = useState<LogFilterState>(DEFAULT_LOG_FILTERS);
  // the time the log range ends at: only moves forward, with the metric polls (no timer of its own)
  const [anchor, setAnchor] = useState(() => Date.now());
  if (metrics.dataUpdatedAt > anchor) setAnchor(metrics.dataUpdatedAt);
  const filters = useEventQueryFilters(state, anchor);
  const events = useMonitoringEvents(filters);
  const memory = events.data?.source === "memory";

  const update = useCallback((patch: Partial<LogFilterState>) => setState((current) => ({ ...current, ...patch })), []);

  const showGroup = useCallback(
    (group: EventGroup) => {
      setState((current) => ({
        ...current,
        // the exact group: source.code@subject (a text search could not combine code and subject)
        fingerprint: group.fingerprint,
        q: "",
        source: "",
        pizzaId: "",
        levels: EVENT_LEVELS.filter((level) => level === group.level || current.levels.includes(level)),
      }));
      document.getElementById(LOG_ID)?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    },
    [reducedMotion],
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <EventsOverview metrics={metrics} />
      <EventGroups onSelect={showGroup} memory={memory} />
      <EventLog
        id={LOG_ID}
        state={state}
        onChange={update}
        onReset={() => setState(DEFAULT_LOG_FILTERS)}
        query={events}
        filters={filters}
        branchNames={branchNames}
      />
    </div>
  );
}
